import type { RawSheetTabs } from "../../src/config/transform";

// Plausible-content fixture, distinct from sheet-tabs.ts's minimal 4-tag unit-test fixture.
// Implements docs/design/2026-08-25-discovery-questions-draft.md exactly: 12 ProblemTags derived
// from the diagnostic axes in docs/design/2026-08-25-pain-point-diagnostic-reference.md, an
// 8-question flow (6 diagnostic + email + one excludeFromFiltering working-style question,
// positioned per the decided "question 2 to 4" rule), and an 8-member active roster plus one
// inactive member, sized so every tag has active coverage (the UC-14 rule) without every member
// covering everything. All content here is made up: no client-approved questions, tags, or Board
// Members yet. Update this file in place as the discovery-questions-draft doc's decisions change;
// keep them in sync.
export const dummyContentSheetTabs: RawSheetTabs = {
  ProblemTags: [
    ["tag_id", "label", "description"],
    ["owner_dependency_knowledge", "Owner holds critical knowledge", "The business depends on information that only lives with the owner"],
    ["decision_authority", "Team lacks decision authority", "The team defers decisions to the owner instead of acting"],
    ["process_not_followed", "Process exists but isn't followed", "A documented process exists but the team routinely works around it"],
    ["accountability_gap", "No accountability for commitments", "Expectations are clear but aren't consistently enforced"],
    ["estimating_capacity", "Estimating and capacity planning is weak", "Timelines and scope are consistently underestimated"],
    ["delegation_fear", "Owner won't fully hand off work", "The owner takes work back rather than letting the team own it"],
    ["wrong_fit_leads", "Leads are the wrong fit", "Inquiries are consistently not a good match for the business"],
    ["conversion_weak", "Right-fit leads aren't converting", "Good-fit prospects reach a consultation or proposal and stall"],
    ["pricing_confidence", "Owner lacks confidence discussing price", "The owner struggles to hold price in sales conversations"],
    ["weak_positioning", "Marketing message doesn't land", "Marketing reaches people but the message doesn't produce the right response"],
    ["low_visibility", "Not enough people find the business", "Too few qualified people encounter the business at all"],
    ["margin_unclear", "Profitability isn't tracked or understood", "The owner can't say with confidence which work is actually profitable"],
  ],
  Questions: [
    ["id", "order", "active", "text", "answer_type", "options", "allows_none", "exclude_from_filtering", "pinned_position", "ask_when"],
    [
      "q1",
      "1",
      "TRUE",
      "Which of these feel true about your business right now? Choose everything that fits.",
      "multi",
      "I'm the bottleneck for almost everything;I'm not making the money I should be;I don't have enough of the right leads;Good prospects don't turn into paying clients;My team and I are stretched thin and things feel chaotic",
      "TRUE",
      "FALSE",
      "1",
      "The fixed opener, asked of everyone before the selector chooses anything.",
    ],
    [
      "q2",
      "2",
      "TRUE",
      "Think of the last time something couldn't move forward without you. What did your team actually need?",
      "single",
      "Information they couldn't find without asking me;My approval, or a decision only I felt comfortable making;This doesn't really happen to us",
      "TRUE",
      "FALSE",
      "",
      "When q1 suggests the visitor may be the bottleneck.",
    ],
    ["q3", "3", "TRUE", "Where should we send your recommendation?", "pii", "", "FALSE", "FALSE", "2", ""],
    [
      "q4",
      "4",
      "TRUE",
      "When something falls through the cracks on a project, what's usually the real reason?",
      "single",
      "We don't actually have a clear process for it;There's a process, but people don't consistently follow it;We underestimated the time or scope;I ended up just doing it myself",
      "TRUE",
      "FALSE",
      "",
      "When execution or delivery looks like the open thread worth narrowing.",
    ],
    [
      "q5",
      "5",
      "TRUE",
      "When a prospect doesn't become a client, what's usually true?",
      "single",
      "They were never really the right fit to begin with;They seemed like a great fit, but something happened in the sales conversation;The price seemed to catch them off guard;I'm honestly not sure, it varies",
      "TRUE",
      "FALSE",
      "",
      "When leads or conversion looks like the open thread worth narrowing.",
    ],
    [
      "q6",
      "6",
      "TRUE",
      "What best describes where you're at with getting found?",
      "single",
      "Not enough people find us in the first place;People find us, but our marketing doesn't land the way we want;This isn't really our issue right now",
      "TRUE",
      "FALSE",
      "",
      "When visibility or positioning looks like the open thread worth narrowing.",
    ],
    [
      "q7",
      "7",
      "TRUE",
      "How confident are you that your pricing is actually profitable?",
      "single",
      "Not confident, I don't really track it closely;I know the numbers, but talking about price with clients is hard;Pretty confident, this isn't my issue",
      "TRUE",
      "FALSE",
      "",
      "When money or margin looks like the open thread worth narrowing.",
    ],
    [
      "q8",
      "8",
      "TRUE",
      "When you're working with someone helping you fix this, what do you want that to feel like?",
      "single",
      "Just tell me exactly what to do and I'll do it;Walk me through it step by step, I want to understand why;Let's figure it out together, I want to stay in the driver's seat;Honestly, whatever gets it fixed fastest",
      "FALSE",
      "TRUE",
      "",
      "Only once the candidate set has narrowing left to do that tags alone can't resolve.",
    ],
  ],
  AnswerTags: [
    ["question_id", "option", "tag_id", "weight"],
    // Q8 (excludeFromFiltering) intentionally has no rows here: its answer never enters the
    // ProblemProfile, so weighting it would be a documented no-op, not a real signal.
    // Q1 (multi) - broad opener, light weights, sharpened by later questions
    ["q1", "I'm the bottleneck for almost everything", "owner_dependency_knowledge", "2"],
    ["q1", "I'm the bottleneck for almost everything", "decision_authority", "2"],
    ["q1", "I'm not making the money I should be", "margin_unclear", "2"],
    ["q1", "I don't have enough of the right leads", "wrong_fit_leads", "2"],
    ["q1", "I don't have enough of the right leads", "low_visibility", "1"],
    ["q1", "Good prospects don't turn into paying clients", "conversion_weak", "2"],
    ["q1", "My team and I are stretched thin and things feel chaotic", "process_not_followed", "1"],
    ["q1", "My team and I are stretched thin and things feel chaotic", "estimating_capacity", "1"],
    // Q2 - knowledge vs. authority
    ["q2", "Information they couldn't find without asking me", "owner_dependency_knowledge", "3"],
    ["q2", "My approval, or a decision only I felt comfortable making", "decision_authority", "3"],
    // Q4 - execution cluster
    ["q4", "We don't actually have a clear process for it", "process_not_followed", "3"],
    ["q4", "There's a process, but people don't consistently follow it", "accountability_gap", "3"],
    ["q4", "We underestimated the time or scope", "estimating_capacity", "3"],
    ["q4", "I ended up just doing it myself", "delegation_fear", "3"],
    // Q5 - marketing vs. sales
    ["q5", "They were never really the right fit to begin with", "wrong_fit_leads", "3"],
    ["q5", "They seemed like a great fit, but something happened in the sales conversation", "conversion_weak", "3"],
    ["q5", "The price seemed to catch them off guard", "pricing_confidence", "2"],
    ["q5", "The price seemed to catch them off guard", "weak_positioning", "1"],
    // Q6 - reach vs. positioning
    ["q6", "Not enough people find us in the first place", "low_visibility", "3"],
    ["q6", "People find us, but our marketing doesn't land the way we want", "weak_positioning", "3"],
    // Q7 - pricing vs. margin
    ["q7", "Not confident, I don't really track it closely", "margin_unclear", "3"],
    ["q7", "I know the numbers, but talking about price with clients is hard", "pricing_confidence", "3"],
  ],
  BoardMembers: [
    ["id", "name", "active", "primary_specialties", "secondary_specialties", "one_liner", "booking_url", "approach", "good_for", "style"],
    [
      "dana",
      "Dana Reyes",
      "TRUE",
      "owner_dependency_knowledge",
      "process_not_followed",
      "Turns what's in your head into a system your team can actually run without you.",
      "https://book.example/dana",
      "Systems-first, hands-on documentation",
      "Owners who are the single point of failure",
      "Methodical",
    ],
    [
      "marcus",
      "Marcus Webb",
      "TRUE",
      "decision_authority",
      "delegation_fear",
      "Helps owners hand off real authority, not just tasks.",
      "https://book.example/marcus",
      "Coaching-led, works the owner's mindset first",
      "Owners who delegate tasks but not decisions",
      "Direct, warm",
    ],
    [
      "priya",
      "Priya Anand",
      "TRUE",
      "accountability_gap",
      "decision_authority",
      "Builds a team that follows through, without you having to police it.",
      "https://book.example/priya",
      "Structured accountability systems",
      "Owners tired of chasing their own team",
      "No-nonsense",
    ],
    [
      "jordan",
      "Jordan Cole",
      "TRUE",
      "estimating_capacity",
      "process_not_followed",
      "Fixes the estimating and workflow gaps that quietly eat your margin.",
      "https://book.example/jordan",
      "Data-driven process redesign",
      "Studios missing deadlines and budgets",
      "Analytical",
    ],
    [
      "renee",
      "Renee Ferris",
      "TRUE",
      "wrong_fit_leads",
      "weak_positioning",
      "Gets your marketing attracting the clients you actually want.",
      "https://book.example/renee",
      "Positioning and message-market fit",
      "Owners drowning in the wrong inquiries",
      "Strategic",
    ],
    [
      "ilya",
      "Ilya Novak",
      "TRUE",
      "low_visibility",
      "weak_positioning",
      "Builds visibility that leads to real inquiries, not just likes.",
      "https://book.example/ilya",
      "Channel strategy and content systems",
      "Owners nobody's finding yet",
      "Energetic",
    ],
    [
      "sofia",
      "Sofia Marchetti",
      "TRUE",
      "conversion_weak",
      "pricing_confidence",
      "Turns good consultations into booked clients, including the pricing conversation.",
      "https://book.example/sofia",
      "Consultative sales coaching",
      "Owners with good leads but a soft close",
      "Warm, confident",
    ],
    [
      "ben",
      "Ben Okafor",
      "TRUE",
      "margin_unclear",
      "pricing_confidence",
      "Shows you which projects actually make money, and how to price the rest.",
      "https://book.example/ben",
      "Numbers-first financial coaching",
      "Owners flying blind on profitability",
      "Calm, precise",
    ],
    [
      "tom",
      "Tom Blake",
      "FALSE",
      "owner_dependency_knowledge",
      "",
      "Retired Board Member, kept in fixtures to exercise inactive-member filtering.",
      "https://book.example/tom",
      "",
      "",
      "",
    ],
  ],
  Settings: [
    ["key", "value"],
    ["enabled", "TRUE"],
    ["confidence_threshold", "0.6"],
    ["tagInclusionFloor", "0.15"],
    ["requireEmailToReveal", "TRUE"],
    ["model", "claude-haiku-4-5"],
    ["monthly_spend_ceiling_usd", "125"],
    ["session_ttl_hours", "24"],
    ["telemetryRetentionDays", "indefinite"],
    ["findABoardMemberUrl", "https://boardroomforcreatives.com/find-a-board-member"],
    ["privacyPolicyUrl", "https://boardroomforcreatives.com/privacy-policy"],
    ["minQuestions", "3"],
    ["maxQuestions", "6"],
    ["selectorModel", "claude-haiku-4-5"],
    ["sessionStartsPerIpPerHour", "20"],
    ["reportViewsPerIpPerHour", "60"],
    ["selectorTimeoutMs", "8000"],
    ["rankingTimeoutMs", "15000"],
    ["maxAlternates", "2"],
  ],
};
